import React, { useState, useEffect, useMemo } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useInventory } from '../../hooks/useInventory';
import { useWarehouses } from '../../hooks/useWarehouses';
import { useProducts } from '../../hooks/useProducts';
import { InventoryMovement, InventoryMovementType } from '../../types/warehouse';
import { Product } from '../../types/product';
import ProductView from '../products/ProductView';
import { storage } from '../../lib/firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import {
  CalendarIcon,
  ArrowUpTrayIcon,
  ArrowDownTrayIcon,
  ArrowsRightLeftIcon,
  AdjustmentsHorizontalIcon,
  ClipboardDocumentCheckIcon,
  BanknotesIcon,
  FunnelIcon,
  ArrowsUpDownIcon,
  BuildingStorefrontIcon,
  ArrowLongUpIcon,
  ArrowLongDownIcon,
  ArrowPathIcon,
  MagnifyingGlassIcon,
  ClipboardIcon,
  EyeIcon,
  DocumentTextIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  InboxArrowDownIcon,
  ArrowTopRightOnSquareIcon,
  MinusIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  BugAntIcon,
  XMarkIcon,
  PlusIcon,
  DocumentArrowUpIcon,
  PaperClipIcon
} from '@heroicons/react/24/outline';
import LoadingScreen from '../common/LoadingScreen';
import DateTimePicker from '../common/DateTimePicker';
import Pagination from '../common/Pagination';
import { format, parse } from 'date-fns';
import { DocumentData, QueryDocumentSnapshot } from 'firebase/firestore';
import toast from 'react-hot-toast';

interface InventoryMovementsListProps {
  productId?: string;
  warehouseId?: string;
  limit?: number;
  onRefresh?: () => void;
}

// Termékkód másolása a vágólapra
const copyToClipboard = (text: string) => {
  navigator.clipboard.writeText(text)
    .then(() => {
      toast.success('Kód másolva a vágólapra!', {
        duration: 2000,
        position: 'bottom-center'
      });
    })
    .catch(err => {
      console.error('Nem sikerült másolni:', err);
      toast.error('Nem sikerült másolni a kódot', {
        duration: 2000,
        position: 'bottom-center'
      });
    });
};

/** A mozgás oka – pl. a selejt is STOCK_OUT, de „Selejt”-ként jelenjen meg. */
const REASON_LABELS: Record<string, string> = {
  scrap: 'Selejt', damaged: 'Sérült', expired: 'Lejárt', lost: 'Hiány',
  worksheet: 'Munkalap', sale: 'Értékesítés', internal: 'Belső felh.',
  stock_count: 'Leltár', correction: 'Korrekció', found: 'Előkerült', opening: 'Nyitókészlet'
};

const InventoryMovementsList: React.FC<InventoryMovementsListProps> = ({
  productId,
  warehouseId,
  limit = 100,
  onRefresh
}) => {
  const { t } = useLanguage();
  const { movements: inventoryMovements, loading, error, fetchMovements, updateInventoryMovement, removeDocumentFromMovement } = useInventory();
  const { warehouses, loading: warehousesLoading } = useWarehouses();
  const { products, loading: productsLoading } = useProducts();
  const [movements, setMovements] = useState<InventoryMovement[]>([]);
  const [fromDate, setFromDate] = useState<Date | null>(null);
  const [toDate, setToDate] = useState<Date | null>(null);
  const [movementType, setMovementType] = useState<InventoryMovementType | null>(null);
  const [selectedWarehouse, setSelectedWarehouse] = useState<string>(warehouseId || '');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [totalItems, setTotalItems] = useState(0);
  const [lastDocs, setLastDocs] = useState<{[page: number]: DocumentData | null}>({});
  const [hasMore, setHasMore] = useState(true);
  
  // ProductView modal state
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [isProductViewOpen, setIsProductViewOpen] = useState(false);
  
  // Document viewing state
  const [selectedDocuments, setSelectedDocuments] = useState<string[]>([]);
  const [isDocumentViewOpen, setIsDocumentViewOpen] = useState(false);
  const [currentViewingMovement, setCurrentViewingMovement] = useState<InventoryMovement | null>(null);
  const [documentToDelete, setDocumentToDelete] = useState<string | null>(null);
  const [isDeletingDocument, setIsDeletingDocument] = useState(false);
  
  // Confirmation modal state
  const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = useState(false);
  const [documentUrlToDelete, setDocumentUrlToDelete] = useState<string | null>(null);
  
  // Document upload state
  const [selectedMovementForUpload, setSelectedMovementForUpload] = useState<InventoryMovement | null>(null);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<{
    id: string;
    name: string;
    size: number;
    type: string;
    url?: string;
    file?: File;
    uploading?: boolean;
  }[]>([]);
  const [dragActive, setDragActive] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);
  
  // Készletmozgások betöltése
  const loadMovements = async (page = 1) => {
    let lastDoc = undefined;
    
    // Ha nem első oldal, és van tárolt lastDoc az előző oldalhoz, használjuk azt
    if (page > 1 && lastDocs[page - 1]) {
      lastDoc = lastDocs[page - 1];
    } else if (page > 1 && !lastDocs[page - 1]) {
      // Ha nincs lastDoc, akkor visszatérünk az első oldalra
      setCurrentPage(1);
      page = 1;
    }
    
    const result = await fetchMovements({
      productId,
      warehouseId: selectedWarehouse || undefined,
      fromDate: fromDate || undefined,
      toDate: toDate || undefined,
      type: movementType || undefined,
      pageSize: itemsPerPage,
      lastDoc,
      sortOrder
    });
    
    if (result.movements.length > 0) {
      // Frissítjük a megjelenített mozgásokat
      setMovements(result.movements);
      
      // Tároljuk az utolsó dokumentumot a következő lapozáshoz
      setLastDocs(prev => ({ ...prev, [page]: result.lastDoc }));
      
      // Ha első oldal, akkor beállítjuk a becsült teljes számot
      if (page === 1) {
        setTotalItems(result.totalCount);
      }
      
      // Ha kevesebb elem jött vissza, mint az oldalméret, akkor nincs több
      setHasMore(result.movements.length >= itemsPerPage);
    } else {
      setHasMore(false);
      if (page === 1) {
        setMovements([]);
      }
    }
  };
  
  // Kezdeti betöltés és szűrő változásoknál
  useEffect(() => {
    // Visszaállítjuk az első oldalra, ha változnak a szűrők
    setCurrentPage(1);
    setLastDocs({});
    loadMovements(1);
  }, [productId, selectedWarehouse, fromDate, toDate, movementType, itemsPerPage, sortOrder]);
  
  // Oldal változásakor
  useEffect(() => {
    loadMovements(currentPage);
  }, [currentPage]);
  
  // Szűrők kezelése
  
  const handleTypeChange = (type: InventoryMovementType | null) => {
    setMovementType(type);
  };
  
  const handleWarehouseChange = (warehouseId: string) => {
    setSelectedWarehouse(warehouseId);
  };
  
  const toggleSortOrder = () => {
    setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
  };
  
  // Direct refresh function that doesn't depend on complex state
  const directRefresh = React.useCallback(async () => {
    console.log('Direct refresh called - resetting pagination and fetching new movements');
    setCurrentPage(1);
    setLastDocs({});
    
    // Call fetchMovements directly with current filters
    const result = await fetchMovements({
      productId,
      warehouseId: selectedWarehouse || undefined,
      fromDate: fromDate || undefined,
      toDate: toDate || undefined,
      type: movementType || undefined,
      pageSize: itemsPerPage,
      sortOrder
    });
    
    if (result.movements.length >= 0) {
      setMovements(result.movements);
      setLastDocs({ 1: result.lastDoc });
      setTotalItems(result.totalCount);
      setHasMore(result.movements.length >= itemsPerPage);
      console.log('Direct refresh completed - loaded', result.movements.length, 'movements');
    }
    
    onRefresh?.(); // Call external refresh if provided
  }, [fetchMovements, productId, selectedWarehouse, fromDate, toDate, movementType, itemsPerPage, sortOrder, onRefresh]);

  // Handle refresh - wrap in useCallback to ensure stable reference
  const handleRefresh = React.useCallback(() => {
    console.log('Handle refresh called');
    setCurrentPage(1);
    setLastDocs({});
    loadMovements(1);
    onRefresh?.(); // Call external refresh if provided
  }, [loadMovements, onRefresh]);
  
  // Always expose BOTH refresh functions globally
  React.useEffect(() => {
    (window as any).refreshInventoryMovements = directRefresh;
    (window as any).refreshInventoryMovementsOld = handleRefresh;
    
    // Cleanup when component unmounts
    return () => {
      delete (window as any).refreshInventoryMovements;
      delete (window as any).refreshInventoryMovementsOld;
    };
  }, [directRefresh, handleRefresh]);
  
  // Pagination logic
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;

  const handlePageChange = (pageNumber: number) => {
    if (pageNumber > currentPage && !hasMore) return;
    setCurrentPage(pageNumber);
  };

  const handleItemsPerPageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setItemsPerPage(Number(e.target.value));
    setCurrentPage(1); // Reset to first page when changing items per page
    setLastDocs({});
  };
  
  // Termék megtekintése
  const handleProductClick = (productId: string) => {
    const product = products.find(p => p.id === productId);
    if (product) {
      setSelectedProduct(product);
      setIsProductViewOpen(true);
    }
  };

  const handleDocumentView = (attachments: string[], movement: InventoryMovement) => {
    setSelectedDocuments(attachments);
    setCurrentViewingMovement(movement);
    setIsDocumentViewOpen(true);
  };

  const downloadDocument = (url: string, filename?: string) => {
    const link = document.createElement('a');
    link.href = url;
    link.download = filename || 'document';
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  
  // Dokumentum feltöltési funkciók
  const handleAddDocument = (movement: InventoryMovement) => {
    setSelectedMovementForUpload(movement);
    setUploadedFiles([]);
    setIsUploadModalOpen(true);
  };

  const handleFileSelect = (files: FileList) => {
    const newFiles = Array.from(files).map(file => ({
      id: `${Date.now()}-${Math.random()}`,
      name: file.name,
      size: file.size,
      type: file.type,
      file,
      uploading: false
    }));
    
    // Fájl validáció
    const validFiles = newFiles.filter(fileData => {
      const maxSize = 10 * 1024 * 1024; // 10MB
      const allowedTypes = [
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'image/jpeg',
        'image/jpg',
        'image/png',
        'image/gif'
      ];
      
      if (fileData.size > maxSize) {
        toast.error(`${fileData.name} túl nagy (max 10MB)`);
        return false;
      }
      
      if (!allowedTypes.includes(fileData.type)) {
        toast.error(`${fileData.name} formátuma nem támogatott`);
        return false;
      }
      
      return true;
    });
    
    setUploadedFiles(prev => [...prev, ...validFiles]);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);
    if (e.dataTransfer.files) {
      handleFileSelect(e.dataTransfer.files);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);
  };

  const removeUploadFile = (fileId: string) => {
    setUploadedFiles(prev => prev.filter(file => file.id !== fileId));
  };

  const uploadFilesToStorage = async (): Promise<string[]> => {
    const uploadPromises = uploadedFiles.map(async (fileData) => {
      if (!fileData.file || fileData.url) return fileData.url || '';

      try {
        const timestamp = Date.now();
        const fileName = `movements/${selectedMovementForUpload?.id || 'unknown'}/${timestamp}_${fileData.file.name}`;
        const fileRef = ref(storage, fileName);
        
        await uploadBytes(fileRef, fileData.file);
        const downloadURL = await getDownloadURL(fileRef);
        
        return downloadURL;
      } catch (error) {
        console.error('Fájl feltöltési hiba:', error);
        toast.error(`Hiba a fájl feltöltésekor: ${fileData.name}`);
        return '';
      }
    });

    const uploadedUrls = await Promise.all(uploadPromises);
    return uploadedUrls.filter(url => url !== '');
  };

  const handleUploadDocuments = async () => {
    if (!selectedMovementForUpload || uploadedFiles.length === 0) {
      toast.error('Válassz ki fájlokat a feltöltéshez!');
      return;
    }

    setUploadLoading(true);
    try {
      // Fájlok feltöltése Storage-ba
      const uploadedUrls = await uploadFilesToStorage();
      
      if (uploadedUrls.length === 0) {
        toast.error('Nem sikerült feltölteni a fájlokat');
        return;
      }

      // Meglévő attachments-hez hozzáadás
      const currentAttachments = selectedMovementForUpload.attachments || [];
      const newAttachments = [...currentAttachments, ...uploadedUrls];

      // Készletmozgás frissítése
      await updateInventoryMovement(selectedMovementForUpload.id, {
        attachments: newAttachments
      });

      toast.success(`${uploadedUrls.length} dokumentum sikeresen hozzáadva!`);
      
      // Lista frissítése
      setTimeout(() => {
        if ((window as any).refreshInventoryMovements) {
          (window as any).refreshInventoryMovements();
        }
      }, 500);

      // Modal bezárása
      setIsUploadModalOpen(false);
      setSelectedMovementForUpload(null);
      setUploadedFiles([]);
      
    } catch (error) {
      console.error('Hiba a dokumentumok hozzáadásakor:', error);
      toast.error('Hiba történt a dokumentumok hozzáadásakor');
    } finally {
      setUploadLoading(false);
    }
  };

  // Dokumentum törlése
  const handleDeleteDocument = async (documentUrl: string) => {
    if (!currentViewingMovement) {
      toast.error('Nincs kiválasztott készletmozgás');
      return;
    }

    // Custom megerősítő modal megnyitása
    setDocumentUrlToDelete(documentUrl);
    setIsConfirmDeleteOpen(true);
  };

  // Dokumentum törlésének megerősítése
  const confirmDeleteDocument = async () => {
    if (!currentViewingMovement || !documentUrlToDelete) {
      return;
    }

    setIsDeletingDocument(true);
    setIsConfirmDeleteOpen(false);
    
    try {
      await removeDocumentFromMovement(currentViewingMovement.id, documentUrlToDelete);
      
      // Frissítjük a megjelenített dokumentumokat
      const updatedDocuments = selectedDocuments.filter(url => url !== documentUrlToDelete);
      setSelectedDocuments(updatedDocuments);
      
      // Ha ez volt az utolsó dokumentum, bezárjuk a modal-t
      if (updatedDocuments.length === 0) {
        setIsDocumentViewOpen(false);
        setCurrentViewingMovement(null);
      }
      
      toast.success('Dokumentum sikeresen törölve!');
      
      // Lista frissítése
      setTimeout(() => {
        if ((window as any).refreshInventoryMovements) {
          (window as any).refreshInventoryMovements();
        }
      }, 500);
      
    } catch (error) {
      console.error('Hiba a dokumentum törlésekor:', error);
      toast.error('Hiba történt a dokumentum törlésekor');
    } finally {
      setIsDeletingDocument(false);
      setDocumentUrlToDelete(null);
    }
  };

  // Megerősítő modal bezárása
  const cancelDeleteDocument = () => {
    setIsConfirmDeleteOpen(false);
    setDocumentUrlToDelete(null);
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };
  
  // Mozgástípus ikon renderelése
  const renderMovementTypeIcon = (type: InventoryMovementType) => {
    switch (type) {
      case InventoryMovementType.STOCK_IN:
        return <ArrowUpTrayIcon className="h-4 w-4 text-green-500 dark:text-green-400" />;
      case InventoryMovementType.STOCK_OUT:
        return <ArrowDownTrayIcon className="h-4 w-4 text-red-500 dark:text-red-400" />;
      case InventoryMovementType.STOCK_TRANSFER:
        return <ArrowsRightLeftIcon className="h-4 w-4 text-blue-500 dark:text-blue-400" />;
      case InventoryMovementType.STOCK_ADJUSTMENT:
        return <AdjustmentsHorizontalIcon className="h-4 w-4 text-yellow-500 dark:text-yellow-400" />;
      case InventoryMovementType.STOCK_INVENTORY:
        return <ClipboardDocumentCheckIcon className="h-4 w-4 text-amber-600 dark:text-amber-400" />;
      case InventoryMovementType.STOCK_RESERVATION:
      case InventoryMovementType.STOCK_RESERVATION_RELEASE:
        return <BanknotesIcon className="h-4 w-4 text-slate-500 dark:text-slate-400" />;
      default:
        return null;
    }
  };
  
  // Mozgástípus megnevezésének renderelése
  const renderMovementTypeName = (type: InventoryMovementType) => {
    return t(`inventory.movementTypes.${type}`);
  };
  
  // Mozgás badge renderelése
  const renderMovementTypeBadge = (type: InventoryMovementType, reason?: string) => {
    let bgColor = 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 border-gray-200 dark:border-gray-600';
    
    switch (type) {
      case InventoryMovementType.STOCK_IN:
        bgColor = 'bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-400 border-green-200 dark:border-green-700';
        break;
      case InventoryMovementType.STOCK_OUT:
        bgColor = 'bg-red-100 dark:bg-red-900/20 text-red-800 dark:text-red-400 border-red-200 dark:border-red-700';
        break;
      case InventoryMovementType.STOCK_TRANSFER:
        bgColor = 'bg-blue-100 dark:bg-blue-900/20 text-blue-800 dark:text-blue-400 border-blue-200 dark:border-blue-700';
        break;
      case InventoryMovementType.STOCK_ADJUSTMENT:
        bgColor = 'bg-yellow-100 dark:bg-yellow-900/20 text-yellow-800 dark:text-yellow-400 border-yellow-200 dark:border-yellow-700';
        break;
      case InventoryMovementType.STOCK_INVENTORY:
        bgColor = 'bg-amber-100 dark:bg-amber-900/20 text-amber-800 dark:text-amber-300 border-amber-200 dark:border-amber-700';
        break;
      case InventoryMovementType.STOCK_RESERVATION:
      case InventoryMovementType.STOCK_RESERVATION_RELEASE:
        bgColor = 'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 border-slate-200 dark:border-slate-600';
        break;
    }
    
    return (
      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border shadow-sm ${bgColor}`}>
        {renderMovementTypeIcon(type)}
        <span className="ml-1.5">{(reason && REASON_LABELS[reason]) || renderMovementTypeName(type)}</span>
      </span>
    );
  };

  return (
    <div className="space-y-4 w-full max-w-full flex-grow">
      {/* Raktárválasztó csempék */}
      {!warehouseId && !warehousesLoading && warehouses.length > 0 && (
        <div className="w-full mb-4">
          <div className="flex flex-wrap gap-2 mt-2">
            <button
              onClick={() => handleWarehouseChange("")}
              className={`inline-flex items-center px-2.5 py-1 rounded-lg text-sm font-medium transition-colors ${
                selectedWarehouse === ""
                  ? "bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300 border border-primary-300 dark:border-primary-700"
                  : "bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 hover:border-gray-400 dark:hover:border-gray-500"
              }`}
            >
              <BuildingStorefrontIcon className="h-4 w-4 mr-2" />
              {t('inventory.movements.allWarehouses')}
            </button>
            
            {warehouses.map(warehouse => (
              <button
                key={warehouse.id}
                onClick={() => handleWarehouseChange(warehouse.id)}
                className={`inline-flex items-center px-2.5 py-1 rounded-lg text-sm font-medium transition-colors ${
                  selectedWarehouse === warehouse.id
                    ? "bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300 border border-primary-300 dark:border-primary-700"
                    : warehouse.isDefault
                      ? "bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 border border-blue-300 dark:border-blue-600 hover:bg-blue-100 dark:hover:bg-blue-900/40"
                      : "bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 hover:border-gray-400 dark:hover:border-gray-500"
                }`}
              >
                <BuildingStorefrontIcon className="h-4 w-4 mr-2" />
                {warehouse.name}
              </button>
            ))}
          </div>
        </div>
      )}
      
      {/* Movement type filters */}
      <div className="w-full mb-4">
        <div className="flex flex-col lg:flex-row gap-3 items-start lg:items-center">
          {/* Első sor: Rendezés és frissítés */}
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={toggleSortOrder}
              className={`inline-flex items-center justify-center px-2.5 py-1 rounded-lg text-sm font-medium ${'bg-blue-100 dark:bg-blue-900/20 text-blue-800 dark:text-blue-400 border-blue-200 dark:border-blue-700 hover:bg-blue-200 dark:hover:bg-blue-900/40'}`}
              title={sortOrder === 'desc' ? t('inventory.movements.sortNewestFirst') : t('inventory.movements.sortOldestFirst')}
            >
              <span className="flex items-center">
                <CalendarIcon className="h-4 w-4 mr-1.5 text-blue-600 dark:text-blue-400" />
                <span className="hidden sm:inline">
                  {sortOrder === 'desc' ? t('inventory.movements.newestFirst') : t('inventory.movements.oldestFirst')}
                </span>
                <span className="sm:hidden">
                  {sortOrder === 'desc' ? 'Legújabb' : 'Legrégebbi'}
                </span>
              </span>
              {sortOrder === 'desc' ? (
                <ArrowLongDownIcon className="h-4 w-4 ml-1.5 text-blue-600 dark:text-blue-400" />
              ) : (
                <ArrowLongUpIcon className="h-4 w-4 ml-1.5 text-blue-600 dark:text-blue-400" />
              )}
            </button>
            
            <button
              type="button"
              onClick={handleRefresh}
              className="inline-flex items-center justify-center px-2.5 py-1 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700"
              title={t('common.refresh')}
            >
              <ArrowPathIcon className="h-4 w-4" />
            </button>
          </div>

          {/* Dátum választók - csak desktop-on inline */}
          <div className="flex flex-col sm:flex-row gap-2 lg:ml-auto w-full lg:w-auto">
            <div className="flex items-center gap-2">
            <DateTimePicker showTime={false} value={fromDate || undefined} maxDate={toDate || undefined} onChange={(d) => setFromDate(d ? new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0) : null)} placeholder="Kezdő dátum" className="w-40" />
            <span className="text-gray-400">–</span>
            <DateTimePicker showTime={false} value={toDate || undefined} minDate={fromDate || undefined} onChange={(d) => setToDate(d ? new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999) : null)} placeholder="Záró dátum" className="w-40" />
          </div>
          </div>
        </div>

        {/* Mozgás típus szűrők - második sor */}
        <div className="flex flex-wrap gap-2 mt-3">
          <button
            onClick={() => handleTypeChange(null)}
            className={`inline-flex items-center px-2.5 py-1 rounded-lg text-sm font-medium transition-colors ${
              movementType === null
                  ? "bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300 border border-primary-300 dark:border-primary-700"
                  : "bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 hover:border-gray-400 dark:hover:border-gray-500"
            }`}
          >
            <FunnelIcon className="h-4 w-4 mr-2" />
            <span className="hidden sm:inline">{t('common.all')}</span>
            <span className="sm:hidden">Mind</span>
          </button>
          
          {Object.values(InventoryMovementType).map((type) => (
            <button
              key={type}
              onClick={() => handleTypeChange(type)}
              className={`inline-flex items-center px-2.5 py-1 rounded-lg text-sm font-medium transition-colors ${
                movementType === type
                  ? "bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300 border border-primary-300 dark:border-primary-700"
                  : "bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 hover:border-gray-400 dark:hover:border-gray-500"
              }`}
            >
              {renderMovementTypeIcon(type)}
              <span className="ml-2 hidden sm:inline">{t(`inventory.movementTypes.${type}`)}</span>
              <span className="ml-2 sm:hidden">
                {type === InventoryMovementType.STOCK_IN && t('inventory.movementTypes.stockInShort')}
                {type === InventoryMovementType.STOCK_OUT && t('inventory.movementTypes.stockOutShort')}
                {type === InventoryMovementType.STOCK_TRANSFER && t('inventory.movementTypes.transferShort')}
                {type === InventoryMovementType.STOCK_ADJUSTMENT && t('inventory.movementTypes.adjustmentShort')}
                {type === InventoryMovementType.STOCK_INVENTORY && t('inventory.movementTypes.inventoryShort')}
                {type === InventoryMovementType.STOCK_RESERVATION && t('inventory.movementTypes.reservationShort')}
                {type === InventoryMovementType.STOCK_RESERVATION_RELEASE && t('inventory.movementTypes.releaseShort')}
              </span>
            </button>
          ))}
        </div>
      </div>
      
      {loading && movements.length === 0 ? (
        <LoadingScreen />
      ) : error ? (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 text-red-700 dark:text-red-400 px-4 py-3 rounded-md w-full">
          {error}
        </div>
      ) : movements.length === 0 ? (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 text-yellow-700 dark:text-yellow-400 px-4 py-3 rounded-md w-full">
          {t('inventory.movements.noMovementsFound')}
        </div>
      ) : (
        <>
          {/* Desktop táblázat */}
          <div className="hidden lg:block overflow-hidden bg-white dark:bg-gray-800 shadow-md rounded-lg border border-gray-200 dark:border-gray-700 w-full">
            <div className="overflow-x-auto w-full">
              <table className="table-fixed min-w-full w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th 
                      scope="col" 
                      className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-[140px]"
                    >
                      <div className="flex items-center">
                        <CalendarIcon className="h-4 w-4 mr-1.5 text-gray-400 dark:text-gray-500" />
                        {t('inventory.movements.date')}
                      </div>
                    </th>
                    <th scope="col" className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-[150px]">
                      {t('inventory.movements.type')}
                    </th>
                    {!productId && (
                      <th scope="col" className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-[200px]">
                        {t('inventory.movements.product')}
                      </th>
                    )}
                    {!selectedWarehouse && !warehouseId && (
                      <th scope="col" className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-[150px]">
                        {t('inventory.movements.warehouse')}
                      </th>
                    )}
                    <th scope="col" className="px-2 py-1.5 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-[100px]">
                      {t('inventory.movements.quantity')}
                    </th>
                    <th scope="col" className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-[180px]">
                      Dokumentumok
                    </th>
                    <th scope="col" className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-[120px]">
                      {t('inventory.movements.user')}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {movements.map((movement) => (
                    <tr key={movement.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-2 py-1.5 text-sm text-gray-500 dark:text-gray-400">
                        <div className="flex items-center">
                          <CalendarIcon className="h-4 w-4 text-gray-400 dark:text-gray-500 mr-2" />
                          {movement.date ? 
                            (() => {
                              try {
                                // Konvertáljuk a dátumot, attól függően, hogy milyen formátumban érkezik
                                let dateObj;
                                
                                if (movement.date instanceof Date) {
                                  dateObj = movement.date;
                                } else if (typeof movement.date === 'string') {
                                  // Próbáljuk meg ISO string-ként értelmezni
                                  dateObj = new Date(movement.date);
                                } else if (typeof movement.date === 'object' && movement.date !== null) {
                                  // Firestore timestamp objektum kezelése
                                  const timestamp = movement.date as { seconds?: number };
                                  if (timestamp.seconds) {
                                    dateObj = new Date(timestamp.seconds * 1000);
                                  } else {
                                    dateObj = new Date(movement.date as any);
                                  }
                                } else {
                                  // Egyéb esetben próbáljuk meg szimplán dátummá konvertálni
                                  dateObj = new Date(movement.date as any);
                                }
                                
                                // Ellenőrizzük, hogy a dátum valóban dátum-e
                                if (isNaN(dateObj.getTime())) {
                                  console.error('Invalid date:', movement.date);
                                  return t('common.invalidDate');
                                }
                                
                                return format(dateObj, 'yyyy-MM-dd HH:mm');
                              } catch (error) {
                                console.error('Date formatting error:', error, 'date:', movement.date);
                                return t('common.invalidDate');
                              }
                            })() : 
                            t('common.invalidDate')
                          }
                        </div>
                      </td>
                      <td className="px-2 py-1.5">
                        {renderMovementTypeBadge(movement.type, (movement as any).reason)}
                      </td>
                      {!productId && (
                        <td className="px-2 py-1.5 text-sm font-medium text-gray-900 dark:text-gray-100 break-words">
                          <div className="flex flex-col">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleProductClick(movement.productId);
                              }}
                              className="text-left hover:text-primary-600 dark:hover:text-primary-400 hover:underline transition-colors cursor-pointer font-medium"
                            >
                              {movement.productName}
                            </button>
                            <div 
                              className="text-xs text-gray-500 dark:text-gray-400 flex items-center cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 hover:text-primary-600 dark:hover:text-primary-400 px-1 py-0.5 rounded transition-colors w-fit group"
                              onClick={(e) => {
                                e.stopPropagation(); 
                                copyToClipboard(movement.productCode);
                              }}
                              title="Kattints a kód másolásához"
                            >
                              <ClipboardIcon className="h-3 w-3 mr-1 group-hover:text-primary-600 dark:group-hover:text-primary-400" />
                              <span>{movement.productCode}</span>
                            </div>
                          </div>
                        </td>
                      )}
                      {!selectedWarehouse && !warehouseId && (
                        <td className="px-2 py-1.5 text-sm text-gray-500 dark:text-gray-400">
                          <div className="flex flex-col">
                            <span className="flex items-center">
                              <BuildingStorefrontIcon className="h-4 w-4 text-gray-400 dark:text-gray-500 mr-1.5" />
                              <span className="text-gray-900 dark:text-gray-100 font-medium">{movement.warehouseName}</span>
                            </span>
                            {movement.targetWarehouseName && (
                              <span className="text-xs text-blue-600 dark:text-blue-400 ml-5 font-medium">
                                → {movement.targetWarehouseName}
                              </span>
                            )}
                          </div>
                        </td>
                      )}
                      <td className="px-2 py-1.5 text-sm text-right font-medium">
                        <span className={
                          movement.type === InventoryMovementType.STOCK_OUT || 
                          movement.type === InventoryMovementType.STOCK_TRANSFER 
                            ? 'text-red-600 dark:text-red-400' 
                            : movement.type === InventoryMovementType.STOCK_IN
                              ? 'text-green-600 dark:text-green-400'
                              : 'text-gray-900 dark:text-gray-100'
                        }>
                          {movement.type === InventoryMovementType.STOCK_OUT || 
                           movement.type === InventoryMovementType.STOCK_TRANSFER 
                            ? '- ' 
                            : movement.type === InventoryMovementType.STOCK_IN
                              ? '+ '
                              : ''}
                          {movement.quantity} {movement.unit}
                        </span>
                      </td>
                      <td className="px-2 py-1.5 text-sm text-gray-500 dark:text-gray-400">
                        <div className="flex flex-col space-y-1">
                          {/* Dokumentum szám és típus */}
                          {movement.documentNumber ? (
                            <div className="flex flex-col">
                              <span className="text-sm text-gray-900 dark:text-gray-100 font-medium">{movement.documentNumber}</span>
                              {movement.documentType && (
                                <span className="text-xs text-gray-500 dark:text-gray-400">
                                  {t(`inventory.documentTypes.${movement.documentType}`)}
                                </span>
                              )}
                            </div>
                          ) : (
                            <span className="text-gray-400 dark:text-gray-500 text-xs">Nincs dokumentumszám</span>
                          )}

                          {/* Feltöltött fájlok és hozzáadás gomb egy sorban */}
                          <div className="flex items-center space-x-2">
                            {movement.attachments && movement.attachments.length > 0 ? (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDocumentView(movement.attachments || [], movement);
                                }}
                                className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-md text-primary-700 dark:text-primary-400 bg-primary-100 dark:bg-primary-900/20 hover:bg-primary-200 dark:hover:bg-primary-900/40 transition-colors"
                                title={`${movement.attachments.length} dokumentum megtekintése`}
                              >
                                <EyeIcon className="h-3 w-3 mr-1" />
                                {movement.attachments.length} fájl
                              </button>
                            ) : (
                              <span className="text-gray-400 dark:text-gray-500 text-xs">Nincs fájl</span>
                            )}

                            {/* Dokumentum hozzáadása - kis zöld ikon */}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleAddDocument(movement);
                              }}
                              className="p-1 text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300 hover:bg-green-50 dark:hover:bg-green-900/20 rounded transition-colors"
                              title="Dokumentum hozzáadása"
                            >
                              <PlusIcon className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      </td>
                      <td className="px-2 py-1.5 text-sm text-gray-500 dark:text-gray-400">
                        <div className="flex items-center space-x-2">
                          <div className="flex flex-col">
                            <span className="text-gray-900 dark:text-gray-100 font-medium">{movement.userName || '-'}</span>
                            {movement.userEmail && (
                              <span className="text-xs text-gray-500 dark:text-gray-400">{movement.userEmail}</span>
                            )}
                          </div>
                          <div 
                            className="relative group cursor-pointer"
                            title={`Movement ID: ${movement.id}\nUser ID: ${movement.userId || 'N/A'}\nUser Name: ${movement.userName || 'N/A'}\nUser Email: ${movement.userEmail || 'N/A'}\nCreated At: ${movement.createdAt ? 
                              (movement.createdAt instanceof Date ? 
                                movement.createdAt.toISOString() : 
                                String(movement.createdAt)
                              ) : 'N/A'
                            }\n\nKattints az ID másolásához!`}
                            onClick={(e) => {
                              e.stopPropagation(); 
                              copyToClipboard(movement.id);
                            }}
                          >
                            <BugAntIcon className="h-3 w-3 text-gray-400 dark:text-gray-500 hover:text-blue-500 dark:hover:text-blue-400 cursor-pointer" />
                          </div>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {loading && (
              <LoadingScreen />
            )}
          </div>

          {/* Mobil kártyák */}
          <div className="lg:hidden space-y-4">
            {movements.map((movement) => (
              <div
                key={movement.id}
                className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden"
              >
                {/* Kártya fejléc */}
                <div className="p-4 border-b border-gray-100 dark:border-gray-700">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      {/* Dátum */}
                      <div className="flex items-center text-sm text-gray-600 dark:text-gray-400 mb-2">
                        <CalendarIcon className="h-4 w-4 mr-2 text-gray-400 dark:text-gray-500" />
                        <span className="font-medium">
                          {movement.date ? 
                            (() => {
                              try {
                                let dateObj;
                                
                                if (movement.date instanceof Date) {
                                  dateObj = movement.date;
                                } else if (typeof movement.date === 'string') {
                                  dateObj = new Date(movement.date);
                                } else if (typeof movement.date === 'object' && movement.date !== null) {
                                  const timestamp = movement.date as { seconds?: number };
                                  if (timestamp.seconds) {
                                    dateObj = new Date(timestamp.seconds * 1000);
                                  } else {
                                    dateObj = new Date(movement.date as any);
                                  }
                                } else {
                                  dateObj = new Date(movement.date as any);
                                }
                                
                                if (isNaN(dateObj.getTime())) {
                                  return t('common.invalidDate');
                                }
                                
                                return format(dateObj, 'MM-dd HH:mm');
                              } catch (error) {
                                return t('common.invalidDate');
                              }
                            })() : 
                            t('common.invalidDate')
                          }
                        </span>
                      </div>

                      {/* Mozgás típus */}
                      <div className="mb-3">
                        {renderMovementTypeBadge(movement.type, (movement as any).reason)}
                      </div>
                    </div>

                    {/* Mennyiség - jobb oldalon prominens */}
                    <div className="text-right">
                      <div className={`text-lg font-bold ${
                        movement.type === InventoryMovementType.STOCK_OUT || 
                        movement.type === InventoryMovementType.STOCK_TRANSFER 
                          ? 'text-red-600 dark:text-red-400' 
                          : movement.type === InventoryMovementType.STOCK_IN
                            ? 'text-green-600 dark:text-green-400'
                            : 'text-gray-900 dark:text-gray-100'
                      }`}>
                        {movement.type === InventoryMovementType.STOCK_OUT || 
                         movement.type === InventoryMovementType.STOCK_TRANSFER 
                          ? '- ' 
                          : movement.type === InventoryMovementType.STOCK_IN
                            ? '+ '
                            : ''}
                        {movement.quantity}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">{movement.unit}</div>
                    </div>
                  </div>

                  {/* Termék információk */}
                  {!productId && (
                    <div className="mb-3">
                      <div className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-1">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleProductClick(movement.productId);
                          }}
                          className="text-left hover:text-primary-600 dark:hover:text-primary-400 hover:underline transition-colors cursor-pointer"
                        >
                          {movement.productName}
                        </button>
                      </div>
                      
                      <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                        <span className="font-medium mr-2">Kód:</span>
                        <div 
                          className="bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded text-xs font-mono flex items-center cursor-pointer hover:bg-primary-50 dark:hover:bg-primary-900/20 hover:text-primary-700 dark:hover:text-primary-400 transition-colors group"
                          onClick={(e) => {
                            e.stopPropagation(); 
                            copyToClipboard(movement.productCode);
                          }}
                          title="Kattints a kód másolásához"
                        >
                          <ClipboardIcon className="h-3 w-3 mr-1 group-hover:text-primary-600 dark:group-hover:text-primary-400" />
                          <span>{movement.productCode}</span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Raktár információk */}
                  {!selectedWarehouse && !warehouseId && (
                    <div className="mb-3">
                      <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                        <BuildingStorefrontIcon className="h-4 w-4 mr-2 text-gray-400 dark:text-gray-500" />
                        <span className="font-medium mr-2">Raktár:</span>
                        <span className="text-gray-900 dark:text-gray-100 font-medium">{movement.warehouseName}</span>
                      </div>
                                              {movement.targetWarehouseName && (
                          <div className="text-sm text-blue-600 dark:text-blue-400 ml-6 font-medium">
                            → {movement.targetWarehouseName}
                          </div>
                        )}
                    </div>
                  )}

                  {/* További adatok */}
                  <div className="space-y-2 text-sm">
                    {movement.documentNumber && (
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600 dark:text-gray-400 font-medium">{t('inventory.movements.document')}:</span>
                        <div className="text-right">
                          <div className="font-medium text-gray-900 dark:text-gray-100">{movement.documentNumber}</div>
                          {movement.documentType && (
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              {t(`inventory.documentTypes.${movement.documentType}`)}
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Felhasználó info mindig megjelenítve debug célból */}
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600 dark:text-gray-400 font-medium">Felhasználó:</span>
                      <div className="flex items-center space-x-2">
                        <div className="flex flex-col">
                          <span className="text-gray-900 dark:text-gray-100 font-medium">{movement.userName || '-'}</span>
                          {movement.userEmail && (
                            <span className="text-xs text-gray-500 dark:text-gray-400">{movement.userEmail}</span>
                          )}
                        </div>
                        <div 
                          className="relative group cursor-pointer"
                          title={`Movement ID: ${movement.id}\nUser ID: ${movement.userId || 'N/A'}\nUser Name: ${movement.userName || 'N/A'}\nUser Email: ${movement.userEmail || 'N/A'}\nCreated At: ${movement.createdAt ? 
                            (movement.createdAt instanceof Date ? 
                              movement.createdAt.toISOString() : 
                              String(movement.createdAt)
                            ) : 'N/A'
                          }\n\nKattints az ID másolásához!`}
                          onClick={(e) => {
                            e.stopPropagation(); 
                            copyToClipboard(movement.id);
                          }}
                        >
                          <BugAntIcon className="h-3 w-3 text-gray-400 dark:text-gray-500 hover:text-blue-500 dark:hover:text-blue-400 cursor-pointer" />
                        </div>
                      </div>
                    </div>

                    {/* Dokumentumok */}
                    {movement.attachments && movement.attachments.length > 0 && (
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600 dark:text-gray-400 font-medium">Dokumentumok:</span>
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDocumentView(movement.attachments || [], movement);
                            }}
                            className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-md text-primary-700 dark:text-primary-400 bg-primary-100 dark:bg-primary-900/20 hover:bg-primary-200 dark:hover:bg-primary-900/40 transition-colors"
                          >
                            <EyeIcon className="h-3 w-3 mr-1" />
                            {movement.attachments.length} db
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleAddDocument(movement);
                            }}
                            className="p-1 text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300 hover:bg-green-50 dark:hover:bg-green-900/20 rounded transition-colors"
                            title="Dokumentum hozzáadása"
                          >
                            <PlusIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Dokumentum hozzáadása - ha nincs feltöltött fájl */}
                    {(!movement.attachments || movement.attachments.length === 0) && (
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600 dark:text-gray-400 font-medium">Dokumentum:</span>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleAddDocument(movement);
                          }}
                          className="p-1 text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300 hover:bg-green-50 dark:hover:bg-green-900/20 rounded transition-colors"
                          title="Dokumentum hozzáadása"
                        >
                          <PlusIcon className="h-4 w-4" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}

            {loading && (
              <LoadingScreen />
            )}
          </div>
        </>
      )}
      
      {movements.length > 0 && (
        <div className="relative z-10">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            itemsPerPage={itemsPerPage}
            totalItems={totalItems}
            indexOfFirstItem={indexOfFirstItem}
            indexOfLastItem={indexOfLastItem > totalItems ? totalItems : indexOfLastItem}
            onPageChange={handlePageChange}
            onItemsPerPageChange={handleItemsPerPageChange}
            hasMore={hasMore}
          />
        </div>
      )}
      
      {selectedProduct && (
        <ProductView
          product={selectedProduct}
          isOpen={isProductViewOpen}
          onClose={() => {
            setIsProductViewOpen(false);
            setSelectedProduct(null);
          }}
        />
      )}

      {/* Document Viewer Modal */}
      {isDocumentViewOpen && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 dark:bg-black dark:bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-full max-w-4xl shadow-lg rounded-lg bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center">
                <DocumentTextIcon className="h-6 w-6 text-primary-600 mr-3" />
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  Feltöltött dokumentumok ({selectedDocuments.length})
                </h3>
              </div>
              <button
                onClick={() => {
                  setIsDocumentViewOpen(false);
                  setCurrentViewingMovement(null);
                  setSelectedDocuments([]);
                  setDocumentToDelete(null);
                }}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-4">
              {selectedDocuments.map((url, index) => {
                const fileName = url.split('/').pop()?.split('?')[0] || `document-${index + 1}`;
                const cleanFileName = fileName.includes('_') ? fileName.split('_').slice(1).join('_') : fileName;
                const fileExtension = cleanFileName.split('.').pop()?.toLowerCase();
                
                return (
                  <div key={index} className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600">
                    <div className="flex items-center space-x-3">
                      <div className="flex-shrink-0">
                        {['jpg', 'jpeg', 'png', 'gif'].includes(fileExtension || '') ? (
                          <div className="w-10 h-10 bg-green-100 dark:bg-green-900/20 rounded-lg flex items-center justify-center">
                            <svg className="w-5 h-5 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                          </div>
                        ) : fileExtension === 'pdf' ? (
                          <div className="w-10 h-10 bg-red-100 dark:bg-red-900/20 rounded-lg flex items-center justify-center">
                            <svg className="w-5 h-5 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                            </svg>
                          </div>
                        ) : (
                          <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/20 rounded-lg flex items-center justify-center">
                            <DocumentTextIcon className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                          {cleanFileName}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {fileExtension?.toUpperCase()} fájl
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => window.open(url, '_blank')}
                        className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-primary-700 dark:text-primary-400 bg-primary-100 dark:bg-primary-900/20 hover:bg-primary-200 dark:hover:bg-primary-900/40 transition-colors"
                      >
                        <EyeIcon className="h-4 w-4 mr-1" />
                        Megtekintés
                      </button>
                      <button
                        onClick={() => downloadDocument(url, cleanFileName)}
                        className="inline-flex items-center px-3 py-1.5 border border-gray-300 dark:border-gray-600 text-xs font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                      >
                        <ArrowTopRightOnSquareIcon className="h-4 w-4 mr-1" />
                        Letöltés
                      </button>
                      <button
                        onClick={() => handleDeleteDocument(url)}
                        disabled={isDeletingDocument}
                        className="inline-flex items-center px-3 py-1.5 border border-red-300 dark:border-red-600 text-xs font-medium rounded-md text-red-700 dark:text-red-400 bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/40 disabled:bg-gray-100 dark:disabled:bg-gray-700 disabled:text-gray-400 dark:disabled:text-gray-500 disabled:cursor-not-allowed transition-colors"
                        title="Dokumentum törlése"
                      >
                        <XMarkIcon className="h-4 w-4 mr-1" />
                        {isDeletingDocument ? 'Törlés...' : 'Törlés'}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            {selectedDocuments.length === 0 && (
              <div className="text-center py-8">
                <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500" />
                <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">Nincsenek dokumentumok</h3>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  Ehhez a készletmozgáshoz nem tartoznak feltöltött dokumentumok.
                </p>
              </div>
            )}

            <div className="flex justify-end pt-4 border-t border-gray-200 dark:border-gray-700 mt-6">
              <button
                onClick={() => {
                  setIsDocumentViewOpen(false);
                  setCurrentViewingMovement(null);
                  setSelectedDocuments([]);
                  setDocumentToDelete(null);
                }}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                Bezárás
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Document Upload Modal */}
      {isUploadModalOpen && selectedMovementForUpload && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 dark:bg-black dark:bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-lg bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center">
                <DocumentArrowUpIcon className="h-6 w-6 text-green-600 dark:text-green-400 mr-3" />
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  Dokumentum hozzáadása
                </h3>
              </div>
              <button
                onClick={() => setIsUploadModalOpen(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            {/* Mozgás információ */}
            <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-medium text-gray-900 dark:text-gray-100">{selectedMovementForUpload.productName}</h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {selectedMovementForUpload.type === InventoryMovementType.STOCK_IN && t('inventory.movementTypes.STOCK_IN')}
                    {selectedMovementForUpload.type === InventoryMovementType.STOCK_OUT && t('inventory.movementTypes.STOCK_OUT')}
                    {selectedMovementForUpload.type === InventoryMovementType.STOCK_TRANSFER && t('inventory.movementTypes.STOCK_TRANSFER')}
                    {selectedMovementForUpload.type === InventoryMovementType.STOCK_ADJUSTMENT && t('inventory.movementTypes.STOCK_ADJUSTMENT')}
                    {selectedMovementForUpload.type === InventoryMovementType.STOCK_INVENTORY && t('inventory.movementTypes.STOCK_INVENTORY')}
                    • {selectedMovementForUpload.quantity} {selectedMovementForUpload.unit}
                    • {selectedMovementForUpload.warehouseName}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {selectedMovementForUpload.date ? 
                      (() => {
                        try {
                          // Konvertáljuk a dátumot, attól függően, hogy milyen formátumban érkezik
                          let dateObj;
                          
                          if (selectedMovementForUpload.date instanceof Date) {
                            dateObj = selectedMovementForUpload.date;
                          } else if (typeof selectedMovementForUpload.date === 'string') {
                            // Próbáljuk meg ISO string-ként értelmezni
                            dateObj = new Date(selectedMovementForUpload.date);
                          } else if (typeof selectedMovementForUpload.date === 'object' && selectedMovementForUpload.date !== null) {
                            // Firestore timestamp objektum kezelése
                            const timestamp = selectedMovementForUpload.date as { seconds?: number };
                            if (timestamp.seconds) {
                              dateObj = new Date(timestamp.seconds * 1000);
                            } else {
                              dateObj = new Date(selectedMovementForUpload.date as any);
                            }
                          } else {
                            // Egyéb esetben próbáljuk meg szimplán dátummá konvertálni
                            dateObj = new Date(selectedMovementForUpload.date as any);
                          }
                          
                          // Ellenőrizzük, hogy a dátum valóban dátum-e
                          if (isNaN(dateObj.getTime())) {
                            console.error('Invalid date:', selectedMovementForUpload.date);
                            return t('common.invalidDate');
                          }
                          
                          return format(dateObj, 'yyyy-MM-dd HH:mm');
                        } catch (error) {
                          console.error('Date formatting error:', error, 'date:', selectedMovementForUpload.date);
                          return t('common.invalidDate');
                        }
                      })() : 
                      t('common.invalidDate')
                    }
                  </p>
                </div>
              </div>
            </div>

            {/* Fájl feltöltés */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                Dokumentumok feltöltése
              </label>
              
              {/* Drag & drop area */}
              <div
                className={`relative border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                  dragActive 
                    ? 'border-green-400 dark:border-green-500 bg-green-50 dark:bg-green-900/20' 
                    : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
                }`}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
              >
                <input
                  type="file"
                  multiple
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif"
                  onChange={(e) => e.target.files && handleFileSelect(e.target.files)}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                />
                <div className="space-y-2">
                  <PaperClipIcon className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500" />
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    <span className="font-medium text-green-600 dark:text-green-400 hover:text-green-500 dark:hover:text-green-300">
                      Kattints a fájlok kiválasztásához
                    </span>
                    {' '}vagy húzd ide őket
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    PDF, Word, JPG, PNG fájlok (max 10MB)
                  </p>
                </div>
              </div>
            </div>

            {/* Kiválasztott fájlok listája */}
            {uploadedFiles.length > 0 && (
              <div className="mb-6">
                <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-3">
                  Kiválasztott fájlok ({uploadedFiles.length})
                </h4>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {uploadedFiles.map((file) => (
                    <div key={file.id} className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-700 rounded border border-gray-200 dark:border-gray-600">
                      <div className="flex items-center space-x-2 flex-1 min-w-0">
                        <DocumentTextIcon className="h-4 w-4 text-gray-500 dark:text-gray-400 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{file.name}</p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">{formatFileSize(file.size)}</p>
                        </div>
                      </div>
                      <button
                        onClick={() => removeUploadFile(file.id)}
                        className="text-red-500 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 p-1"
                        title="Fájl eltávolítása"
                      >
                        <XMarkIcon className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Gombok */}
            <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => setIsUploadModalOpen(false)}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                disabled={uploadLoading}
              >
                Mégse
              </button>
              <button
                onClick={handleUploadDocuments}
                disabled={uploadedFiles.length === 0 || uploadLoading}
                className="px-4 py-2 bg-green-600 dark:bg-green-500 text-white rounded-md hover:bg-green-700 dark:hover:bg-green-600 disabled:bg-gray-400 dark:disabled:bg-gray-600 disabled:cursor-not-allowed transition-colors"
              >
                {uploadLoading ? 'Feltöltés...' : `${uploadedFiles.length} fájl feltöltése`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      {isConfirmDeleteOpen && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 dark:bg-black dark:bg-opacity-50 overflow-y-auto h-full w-full z-[60]">
          <div className="relative top-20 mx-auto p-6 border w-full max-w-md shadow-lg rounded-lg bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center">
                <ExclamationTriangleIcon className="h-6 w-6 text-red-600 dark:text-red-400 mr-3" />
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  Dokumentum törlése
                </h3>
              </div>
              <button
                onClick={cancelDeleteDocument}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="mb-6">
              <p className="text-sm text-gray-700 dark:text-gray-300">
                Biztosan törölni szeretnéd ezt a dokumentumot?
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                Ez a művelet nem vonható vissza.
              </p>
            </div>

            <div className="flex justify-end space-x-3">
              <button
                onClick={cancelDeleteDocument}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                Mégse
              </button>
              <button
                onClick={confirmDeleteDocument}
                disabled={isDeletingDocument}
                className="px-4 py-2 bg-red-600 dark:bg-red-500 text-white rounded-md hover:bg-red-700 dark:hover:bg-red-600 disabled:bg-red-400 dark:disabled:bg-red-600 disabled:cursor-not-allowed transition-colors"
              >
                {isDeletingDocument ? 'Törlés...' : 'Törlés'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Segédfüggvény a típusnak megfelelő színosztály meghatározásához
const getBgColorForType = (type: InventoryMovementType) => {
  switch (type) {
    case InventoryMovementType.STOCK_IN:
      return 'bg-green-100 text-green-800 border-green-200 shadow-sm';
    case InventoryMovementType.STOCK_OUT:
      return 'bg-red-100 text-red-800 border-red-200 shadow-sm';
    case InventoryMovementType.STOCK_TRANSFER:
      return 'bg-blue-100 text-blue-800 border-blue-200 shadow-sm';
    case InventoryMovementType.STOCK_ADJUSTMENT:
      return 'bg-yellow-100 text-yellow-800 border-yellow-200 shadow-sm';
    case InventoryMovementType.STOCK_INVENTORY:
      return 'bg-amber-100 text-amber-800 border-amber-200 shadow-sm';
    case InventoryMovementType.STOCK_RESERVATION:
    case InventoryMovementType.STOCK_RESERVATION_RELEASE:
      return 'bg-slate-100 text-slate-700 border-slate-200 shadow-sm';
    default:
      return 'bg-gray-100 text-gray-800 border-gray-300 shadow-sm';
  }
};

export default InventoryMovementsList; 